import {
  apiGet,
  apiSend,
  type BulkResult,
  type DeleteResult,
  type SavedVoucher,
  type VoucherDetail,
  type VoucherListRow,
} from './api.js';
import { auth } from './actor.js';
import type { VoucherTypeCode } from './voucher-config.js';
import type { VoucherPayload } from './voucher-model.js';

/**
 * Every call the voucher screens make against the ledger.
 *
 * This module used to be a fake: an in-memory counter standing in for
 * `next_document_number()` and an array standing in for `voucher_lines`. It is
 * now nine `fetch`es. What survived the change is the shape — the screen still
 * asks for a number, saves a voucher and lists what it saved — because that
 * shape was designed against the real thing.
 *
 * Nothing here validates. The form checked the rules before calling, the
 * database checks them again on arrival, and this layer's only job is to carry
 * the failure message from the second back to the first intact.
 */

// `setActingUser` was re-exported from here so `App` could tell the write layer
// which email to attach. There is no email to attach: the acting user is
// resolved by the server from the session cookie. `actor.ts` explains what is
// left of that mechanism and why the function it kept is not deleted.

// ── Numbering ────────────────────────────────────────────────────────────────

/**
 * What the next voucher number *would* be.
 *
 * A peek, not an allocation: two accountants opening the form see the same
 * number and whoever saves first gets it. The number that ends up on the
 * document is the one `next_document_number()` hands out inside the posting
 * transaction, which is why `saveVoucher` returns it rather than the form
 * assuming the peek was right.
 */
export async function peekVoucherNumber(type: VoucherTypeCode): Promise<string> {
  const { number } = await apiGet<{ type: string; number: string }>('/next-number', { type });
  return number;
}

// ── Write ────────────────────────────────────────────────────────────────────

/** Where a save should leave the voucher. */
export type SaveStatus = 'DRAFT' | 'PENDING_APPROVAL' | 'POSTED';

/**
 * Save a voucher.
 *
 * `DRAFT` is legal in a closed period and exempt from the balance check — it has
 * not reached the ledger. `PENDING_APPROVAL` hands it to a checker. `POSTED`
 * runs the whole gauntlet in `0015_invariants.sql`, and a rejection arrives as
 * an `ApiError` carrying the trigger's own sentence.
 */
export function saveVoucher(payload: VoucherPayload, status: SaveStatus): Promise<SavedVoucher> {
  return apiSend<SavedVoucher>('POST', '/vouchers', payload, auth({ status }));
}

/**
 * Delete a draft, or reverse a posted voucher.
 *
 * One call for both because the ledger only offers one: which of the two happens
 * is decided by the voucher's status, server-side, and reported back in
 * `action`. The caller shows the message rather than guessing in advance.
 */
export function deleteVoucher(
  type: VoucherTypeCode,
  voucherNo: string,
  reason?: string,
): Promise<DeleteResult> {
  return apiSend<DeleteResult>(
    'DELETE',
    `/vouchers/${type}/${encodeURIComponent(voucherNo)}`,
    undefined,
    auth({ reason }),
  );
}

/** Save a whole file of vouchers. Each one stands or falls on its own. */
export function saveVoucherBatch(
  vouchers: VoucherPayload[],
  status: SaveStatus,
): Promise<BulkResult> {
  return apiSend<BulkResult>('POST', '/vouchers/bulk', { vouchers }, auth({ status }));
}

// ── Read ─────────────────────────────────────────────────────────────────────

/** The register for one voucher type, newest first — what the navigator walks. */
export async function listVouchers(type: VoucherTypeCode): Promise<VoucherListRow[]> {
  const { vouchers } = await apiGet<{ type: string; vouchers: VoucherListRow[] }>('/vouchers', {
    type,
  });
  return vouchers;
}

/** One voucher with its lines, ready for `draftFromLoaded`. */
export function loadVoucher(type: VoucherTypeCode, voucherNo: string): Promise<VoucherDetail> {
  return apiGet<VoucherDetail>(`/vouchers/${type}/${encodeURIComponent(voucherNo)}`);
}
